import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Sparkles, Landmark, Dumbbell, Waves, UtensilsCrossed, Umbrella } from "lucide-react";

const units = [
  { count: "78", type: "1+0", label: "ERGONOMİK" },
  { count: "298", type: "1+1", label: "FERAH" },
  { count: "46", type: "2+1", label: "KONFORLU" },
  { count: "8", type: "3+1", label: "GENİŞ" },
];

export default function ProjectOverview() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="project-overview"
      data-testid="project-overview-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 bg-[#F0EBE3]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-4 mb-6"
        >
          <div className="w-8 h-[2px] bg-[#c13a05]" />
          <span className="font-sub text-[#c13a05] text-[11px] tracking-[0.25em] font-bold">
            PROJEYE GENEL BAKIŞ
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-4xl sm:text-5xl font-normal text-[#1A1A1A] tracking-tight leading-tight mb-16"
        >
          <span className="font-sub font-bold">430</span> <span className="font-heading">Seçkin</span><br />
          <span className="font-heading italic text-[#7d7441]">Yaşam Alanı</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-7 img-reveal"
          >
            <div className="relative h-[350px] md:h-[480px] overflow-hidden">
              <img src="/images/renders/render6.jpg" alt="FINORA Sahil Projesi" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8">
                <p className="font-sub text-white/60 text-[10px] tracking-[0.2em] font-bold mb-2">DENİZE SIFIR</p>
                <p className="font-heading text-white text-3xl font-normal">Finike, Antalya</p>
              </div>
              <div className="absolute top-6 right-6 opacity-30">
                <img src="/images/brandmarks/brandmark-beyaz.svg" alt="" className="w-8" />
              </div>
            </div>
          </motion.div>

          <div className="md:col-span-5 flex flex-col gap-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="bg-[#FAF7F3] p-8 flex-1 flex flex-col justify-center"
            >
              <p className="font-sub text-[#c13a05] text-[10px] tracking-[0.2em] font-bold mb-4">A BLOK</p>
              <p className="font-number text-6xl text-[#1A1A1A] tracking-tight">194</p>
              <p className="font-body text-[#5A5A58] text-sm mt-2 font-light">daire</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-[#FAF7F3] p-8 flex-1 flex flex-col justify-center"
            >
              <p className="font-sub text-[#c13a05] text-[10px] tracking-[0.2em] font-bold mb-4">B BLOK</p>
              <p className="font-number text-6xl text-[#1A1A1A] tracking-tight">236</p>
              <p className="font-body text-[#5A5A58] text-sm mt-2 font-light">daire</p>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8">
          {units.map((unit, i) => {
            const colors = ["#c13a05", "#d5bd9a", "#7d7441", "#9ac2cc"];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 + i * 0.1 }}
                data-testid={`unit-type-${unit.type}`}
                className="bg-[#FAF7F3] p-6 md:p-8 hover:shadow-md transition-all duration-300 border-t-2"
                style={{ borderTopColor: colors[i] }}
              >
                <p className="font-number text-4xl md:text-5xl text-[#1A1A1A] mb-1 tracking-tight">{unit.count}</p>
                <p className="font-sub text-sm font-bold mb-1" style={{ color: colors[i] }}>{unit.type}</p>
                <p className="font-body text-xs text-[#5A5A58] tracking-[0.1em] font-light">{unit.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Amenities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-12 bg-[#FAF7F3] p-8 md:p-10"
        >
          <p className="font-sub text-[#c13a05] text-[10px] tracking-[0.2em] font-bold mb-6">SOSYAL DONATLAR</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
            {[
              { icon: Sparkles, label: "SPA" },
              { icon: Landmark, label: "HAMAM" },
              { icon: Dumbbell, label: "FİTNESS" },
              { icon: Waves, label: "HAVUZ" },
              { icon: UtensilsCrossed, label: "RESTORAN" },
              { icon: Umbrella, label: "PLAJ" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <item.icon className="w-6 h-6 text-[#c13a05] mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-sub text-[10px] text-[#5A5A58] tracking-[0.1em] font-bold">{item.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
